import React, { useEffect, useState, useRef, useMemo } from "react";
import Scrollbars from "react-custom-scrollbars-2";
import { Link, useLocation } from "react-router-dom";
import ImageWithBasePath from "../../core/common/imageWithBasePath";
import "react-modal-video/scss/modal-video.scss";
import CollapseHeader from "../../core/common/collapse-header/collapse-header";
import {
  initializeSocket,
  initializeCallingSocket,
  disconnectChatSocket,
  getSocket,
} from "./chatServices";
import { Modal } from "react-bootstrap";
import { getAllUsers, getUserChannels } from "./chatServices";
import { useSelector } from "react-redux";
import EmojiPicker, { EmojiClickData } from "emoji-picker-react";
import api from "../../services/api";
import { motion, AnimatePresence } from "framer-motion";
import "./audioCall.css";
import "./premium-chat.css";

// ✅ Use global call context — NOT local useWebRTCCall
import { useGlobalCall } from "./GlobalCallContext";

// ─── Types ────────────────────────────────────────────────────────────────────

interface ChatAttachment {
  name: string;
  path: string;
  type: string;
  size: number;
}

interface ChatUser {
  id: string;
  name: string;
  senderId?: string;
  avatar: string;
  lastMessage?: string;
  time?: string;
  unreadCount?: number;
  isOnline: boolean;
  isTyping?: boolean;
  isPinned?: boolean;
  isFavorite?: boolean;
  lastMessageStatus?: "sent" | "delivered" | "read";
  lastMessageType?:
  | "document"
  | "audio"
  | "video"
  | "image"
  | "text"
  | "call"
  | "missed-call"
  | "video-call";
  isGroup?: boolean;
  members?: ChatUser[];
  createdBy?: string;
  company_id?: number;
  account_type?: string;
  status?: number;
}

interface ChatMessage {
  id: string;
  senderId: string;
  text: string;
  time: string;
  status?: "sent" | "delivered" | "read";
  type: "text" | "image" | "video" | "document" | "audio" | "call";
  attachments?: ChatAttachment[];
  callDetails?: {
    callType: "audio" | "video";
    callStatus: "missed" | "answered" | "rejected";
    duration?: number;
    callerId: string;
  };
}

interface GroupFormData {
  name: string;
  members: string[];
  avatar?: string | File;
}

// ─── Component ────────────────────────────────────────────────────────────────

const getSenderColor = (name?: string) => {
  if (!name) return "#6c757d";
  const colors = [
    "#d32f2f", "#c2185b", "#7b1fa2", "#512da8",
    "#303f9f", "#1976d2", "#0288d1", "#0097a7",
    "#00796b", "#388e3c", "#689f38", "#afb42b",
    "#fbc02d", "#ffa000", "#f57c00", "#e64a19"
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const Chat: React.FC = () => {
  const _user = useSelector((state: any) => state?.user.user);
  const currentUserId = _user?.userDetails?.id;
  const baseUrl = process.env.REACT_APP_API_URL;

  // ── Chat State ──────────────────────────────────────────────────────────────
  const [editingMessage, setEditingMessage] = useState<ChatMessage | null>(
    null,
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState<ChatMessage | null>(
    null,
  );
  const [showDeleteChatModal, setShowDeleteChatModal] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeUser, setActiveUser] = useState<ChatUser | null>(null);
  const activeUserRef = useRef(activeUser);
  useEffect(() => { activeUserRef.current = activeUser; }, [activeUser]);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [users, setUsers] = useState<ChatUser[]>([]);
  const [allContacts, setAllContacts] = useState<ChatUser[]>([]);
  const [isFirstTimeChat, setIsFirstTimeChat] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [showGroupEditModal, setShowGroupEditModal] = useState(false);
  const [showGroupMembersModal, setShowGroupMembersModal] = useState(false);
  const [groupForm, setGroupForm] = useState<GroupFormData>({
    name: "",
    members: [],
    avatar: undefined,
  });
  const [groupSearchTerm, setGroupSearchTerm] = useState("");
  const [recentChatsSearchTerm, setRecentChatsSearchTerm] = useState("");
  const [selectedLetter, setSelectedLetter] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [contactSearchTerm, setContactSearchTerm] = useState("");
  const [messageInput, setMessageInput] = useState("");
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadingGroupAvatar, setUploadingGroupAvatar] = useState(false);
  const [filePreviews, setFilePreviews] = useState<
    Array<{ name: string; size: number; type: string; preview?: string }>
  >([]);
  const [groupAvatarPreview, setGroupAvatarPreview] = useState<string | null>(
    null,
  );
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const showScrollToBottomRef = useRef(showScrollToBottom);
  useEffect(() => { showScrollToBottomRef.current = showScrollToBottom; }, [showScrollToBottom]);
  const [unreadBottomCount, setUnreadBottomCount] = useState(0);

  // ── Refs ────────────────────────────────────────────────────────────────────
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<any>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const groupAvatarInputRef = useRef<HTMLInputElement>(null);
  const notificationSoundRef = useRef<HTMLAudioElement | null>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const allContactsRef = useRef<ChatUser[]>([]);

  const { startCall, formatDuration, onCallMessageRef, callState } = useGlobalCall();
  const isCallActive = callState.status !== "idle" && callState.status !== "ended";

  const getCallText = (msg: any) => {
    if (!msg) return "";
    if (msg.type === "call") {
      const details = msg.metadata?.callDetails || msg.callDetails;
      if (details) {
        const isCaller = String(details.callerId) === String(currentUserId);
        if (details.callStatus === "missed") return isCaller ? "no answer" : "Miss call";
        if (details.callStatus === "rejected") return isCaller ? "Call Rejected" : "You declined the call";
        if (details.callStatus === "answered") return `Audio call · ${formatDuration(details.duration ?? 0)}`;
      }
      return msg.content || "Audio call";
    }
    return msg.content || (msg.attachments?.length ? `📎 ${msg.attachments[0].name}` : "");
  };

  useEffect(() => {
    onCallMessageRef.current = (
      channelId: string,
      status: "missed" | "answered" | "rejected",
      duration?: number,
      callerId?: string,
    ) => {
      // Use the actual caller ID passed from GlobalCallContext
      const actualCallerId = callerId || String(currentUserId);
      const isCaller = String(actualCallerId) === String(currentUserId);

      const texts: Record<string, string> = {
        missed: isCaller ? "no answer" : "Miss call",
        rejected: isCaller ? "Call Rejected" : "You declined the call",
        answered: `Audio call · ${formatDuration(duration ?? 0)}`,
      };
      const text = texts[status] ?? "Call ended";

      const callMsg: ChatMessage = {
        id: `call-${Date.now()}`,
        senderId: actualCallerId,
        text,
        time: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        status: "sent",
        type: "call",
        callDetails: {
          callType: "audio",
          callStatus: status,
          duration,
          callerId: actualCallerId,
        },
      };

      if (activeUser?.id === channelId) {
        setChatMessages((prev) => {
          const callTime = parseInt(callMsg.id.replace("call-", ""), 10);
          const isDuplicate = prev.some((m) => {
            if (m.type !== "call" || m.senderId !== callMsg.senderId) return false;
            const existingTime = m.id.startsWith("call-")
              ? parseInt(m.id.replace("call-", ""), 10)
              : 0;
            return (
              !isNaN(existingTime) &&
              !isNaN(callTime) &&
              Math.abs(existingTime - callTime) < 5000
            );
          });
          return isDuplicate ? prev : [...prev, callMsg];
        });
      }

      // Update sidebar last message
      setUsers((prev: any) => {
        const mapped = prev.map((u: any) =>
          u.id === channelId
            ? {
              ...u,
              senderId: currentUserId,
              lastMessage: text,
              time: callMsg.time,
              lastMessageType: "call",
            }
            : u,
        );
        const targetIdx = mapped.findIndex((u: any) => u.id === channelId);
        if (targetIdx > 0) {
          const item = mapped.splice(targetIdx, 1)[0];
          mapped.unshift(item);
        }
        return mapped;
      });
    };

    return () => {
      onCallMessageRef.current = null;
    };
  }, [currentUserId, formatDuration, onCallMessageRef, activeUser]);


  // ✅ Helper: start a call from a ChatUser — uses global startCall
  const handleStartCall = (user: ChatUser) => {
    if (user.isGroup) return;
    const target = user.members?.find(
      (m) => String(m.id) !== String(currentUserId),
    );
    if (!target) {
      setError("Cannot determine target user");
      return;
    }
    startCall(
      { id: String(target.id), name: user.name, avatar: user.avatar },
      user.id,
    );
  };

  // ── Avatar helpers ──────────────────────────────────────────────────────────
  const isMissingAvatar = (src?: string | null) => {
    if (!src) return true;
    const s = String(src).trim().toLowerCase();
    return (
      !s || s === "null" || s === "undefined" || s.includes("default-avatar")
    );
  };

  const firstChar = (name?: string | null) =>
    name?.trim()?.charAt(0)?.toUpperCase() || "?";

  const renderAvatar = ({
    src,
    name,
    isGroup = false,
    size = 40,
    className = "",
    roundedClass = "rounded-circle",
    baseUrl: bUrl = "",
  }: {
    src?: string | null;
    name?: string | null;
    isGroup?: boolean;
    size?: number;
    className?: string;
    roundedClass?: string;
    baseUrl?: string;
  }) => {
    const valid = !isMissingAvatar(src);
    if (valid) {
      const finalSrc = isGroup ? `${bUrl}chatService${src}` : (src as string);
      return isGroup ? (
        <img
          src={finalSrc}
          className={`${roundedClass} ${className}`}
          alt={name || ""}
          style={{ width: size, height: size, objectFit: "cover" }}
        />
      ) : (
        <ImageWithBasePath
          src={finalSrc}
          className={`${roundedClass} ${className}`}
          alt={name || ""}
        />
      );
    }
    return (
      <div
        className={`${roundedClass} ${className} d-inline-flex align-items-center justify-content-center`}
        style={{
          width: size,
          height: size,
          fontWeight: 700,
          fontSize: Math.floor(size * 0.45),
          background: "#EFF3F6",
          color: "#2C3E50",
          userSelect: "none",
        }}
        aria-label={name || "User"}
        title={name || "User"}
      >
        {firstChar(name)}
      </div>
    );
  };

  // ── Debounce search ─────────────────────────────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearchTerm(contactSearchTerm),
      300,
    );
    return () => clearTimeout(timer);
  }, [contactSearchTerm]);

  // ── Notification sound ──────────────────────────────────────────────────────
  useEffect(() => {
    notificationSoundRef.current = new Audio("/assets/sounds/notification.mp3");
    notificationSoundRef.current.preload = "auto";
    return () => {
      notificationSoundRef.current = null;
    };
  }, []);

  // ── Click outside emoji picker ──────────────────────────────────────────────
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target as Node)
      ) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ── Body class + socket init ────────────────────────────────────────────────
  const useBodyClass = (className: string) => {
    const location = useLocation();
    useEffect(() => {
      initializeSocket();
      initializeCallingSocket();
      if (location.pathname === "/application/chat") {
        document.body.classList.add(className);
      } else {
        document.body.classList.remove(className);
      }
      return () => {
        document.body.classList.remove(className);
      };
    }, [location.pathname, className]);
  };
  useBodyClass("app-chat");

  // ── Memoized data ───────────────────────────────────────────────────────────
  const memoizedUsers = useMemo(() => users, [users]);
  const memoizedContacts = useMemo(() => allContacts, [allContacts]);
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

  const filteredContacts = useMemo(() => {
    let filtered = memoizedContacts.filter(c => c.id !== currentUserId);

    if (debouncedSearchTerm || groupSearchTerm) {
      const searchTerm = (groupSearchTerm || debouncedSearchTerm).toLowerCase();
      filtered = filtered.filter(c =>
        c.name.toLowerCase().includes(searchTerm)
      );
    }

    if (selectedLetter) {
      filtered = filtered.filter(c =>
        c.name.toUpperCase().startsWith(selectedLetter)
      );
    }

    return filtered;
  }, [
    memoizedContacts,
    debouncedSearchTerm,
    selectedLetter,
    groupSearchTerm,
    currentUserId,
  ]);

  // ── File helpers ────────────────────────────────────────────────────────────
  const getFileIcon = (type: string) => {
    if (type.startsWith("image/")) return "ti ti-photo";
    if (type.startsWith("video/")) return "ti ti-video";
    if (type.startsWith("audio/")) return "ti ti-music";
    if (type.includes("pdf")) return "ti ti-file-text";
    if (type.includes("word")) return "ti ti-file-text";
    if (type.includes("excel")) return "ti ti-file-spreadsheet";
    if (type.includes("zip") || type.includes("rar")) return "ti ti-file-zip";
    return "ti ti-file";
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const files = Array.from(e.target.files);
    setFilePreviews(
      files.map((file) => ({
        name: file.name,
        size: file.size,
        type: file.type,
        preview: file.type.startsWith("image/")
          ? URL.createObjectURL(file)
          : undefined,
      })),
    );
  };

  const handleGroupAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    if (groupAvatarPreview) URL.revokeObjectURL(groupAvatarPreview);
    setGroupAvatarPreview(URL.createObjectURL(file));
    setGroupForm((prev) => ({ ...prev, avatar: file }));
  };

  const removeFilePreview = (index: number) => {
    setFilePreviews((prev) => prev.filter((_, i) => i !== index));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const uploadFiles = async (files: FileList) => {
    if (!files || files.length === 0) return [];
    setUploadingFiles(true);
    try {
      const formData = new FormData();
      Array.from(files).forEach((file) => formData.append("files", file));
      const response = await api.post(
        "/chatService/api/chat/upload",
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );
      if (!response.data) throw new Error("No data received from server");
      return response.data.files.map((file: any) => ({
        name: file.originalName,
        path: file.path,
        type: file.type,
        size: file.size,
      }));
    } catch (error: any) {
      setError(
        error.response?.data?.error ||
        error.message ||
        "Failed to upload files",
      );
      return [];
    } finally {
      setUploadingFiles(false);
      setFilePreviews([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const uploadGroupAvatar = async (file: File | string | undefined) => {
    if (!file || typeof file === "string") return file;
    setUploadingGroupAvatar(true);
    try {
      const formData = new FormData();
      formData.append("files", file);
      const response = await api.post(
        "/chatService/api/chat/upload",
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );
      if (!response.data?.files?.length) throw new Error("No data received");
      return response.data.files[0].path;
    } catch (error: any) {
      setError(
        error.response?.data?.error ||
        error.message ||
        "Failed to upload group avatar",
      );
      return null;
    } finally {
      setUploadingGroupAvatar(false);
      if (groupAvatarInputRef.current) groupAvatarInputRef.current.value = "";
    }
  };

  // ── Data mapping ────────────────────────────────────────────────────────────
  const mapChatToChatUser = (chat: any, contacts: ChatUser[]): ChatUser => {
    const participantIds = chat.participants || chat.memberIds || [];
    const me = String(currentUserId);
    const otherParticipants = participantIds
      .filter((id: number) => String(id) !== me)
      .map((id: number) => contacts.find((c) => c.id === String(id)))
      .filter(Boolean) as ChatUser[];
    const participantNames = otherParticipants.map((c) => c.name).join(", ");
    const oneToOneAvatar =
      otherParticipants.length === 1 ? otherParticipants[0].avatar || "" : "";
    const members =
      participantIds.map((id: number) => {
        const c = contacts.find((x) => x.id === String(id));
        return {
          id: String(id),
          name: c ? c.name : `User ${id}`,
          avatar: c?.avatar || "",
          isOnline: !!c?.isOnline,
        };
      }) || [];
    const directPeerOnline =
      !chat.type || chat.type === "direct"
        ? otherParticipants.some((c) => !!c.isOnline)
        : false;
    const groupOnline =
      chat.type === "group" ? members.some((m: any) => m.isOnline) : false;
    return {
      id: String(chat.id),
      name:
        chat.type === "group"
          ? chat.name || `Group ${chat.channelId}`
          : participantNames,
      senderId: chat.lastMessage?.senderId
        ? String(chat.lastMessage.senderId)
        : "",
      avatar:
        chat.avatar ??
        (chat.type === "group"
          ? (chat.metadata?.avatar ?? "")
          : oneToOneAvatar),
      lastMessage: getCallText(chat.lastMessage),
      time: chat.lastMessage
        ? new Date(chat.lastMessage.createdAt).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })
        : "",
      unreadCount: chat.unreadCount || 0,
      isOnline: chat.type === "group" ? groupOnline : directPeerOnline,
      isTyping: false,
      isPinned: false,
      isFavorite: false,
      lastMessageStatus: chat.lastMessage?.status || "sent",
      lastMessageType: chat.lastMessage?.type || "text",
      isGroup: chat.type === "group",
      createdBy: chat.createdBy ? String(chat.createdBy) : "",
      members,
    };
  };

  const mapUserToChatUser = (user: any): ChatUser => ({
    id: user.key.toString(),
    name: user.Name,
    avatar: user.Image ?? "",
    lastMessage: "",
    time: "",
    unreadCount: 0,
    isOnline: user.Status === "Active",
    isTyping: false,
    isPinned: false,
    isFavorite: false,
    lastMessageStatus: "sent",
    lastMessageType: "text",
    isGroup: false,
    members: [],
    company_id: user.company_id,
    account_type: user.account_type,
    status: user.Status
  });

  // ── Helper: map backend message to ChatMessage (fixes persistence) ───────
  const mapBackendMessage = (msg: any): ChatMessage => ({
    id: msg.id.toString(),
    senderId: msg.senderId.toString(),
    text: msg.content || "",
    time: new Date(msg.createdAt).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    }),
    status: msg.status || "sent",
    type: msg.type || "text",
    attachments: msg.attachments || [],
    callDetails: msg.metadata?.callDetails || msg.callDetails || undefined,
  });

  // ── Socket: main chat ───────────────────────────────────────────────────────
  useEffect(() => {
    const socket = getSocket();
    socket.emit("join-channels");
    fetchInitialData();
    const handleSuccess = (response: any) => {
      if (response.channels) console.log("Joined channels:", response.channels);
    };
    const handleError = (error: any) => {
      console.error("Socket error:", error.message);
      setError(error.message);
    };

    socket.on("success", handleSuccess);
    socket.on("group-channel-created", handleGroupChannelCreated);
    socket.on("message-edited", handleMessageEdited);
    socket.on("message-deleted", handleMessageDeleted);
    socket.on("channel-deleted", handleChannelDeleted);
    socket.on("channel-updated", handleChannelUpdated);
    socket.on("group-updated", handleGroupUpdated);
    socket.on("message-status-updated", handleMessageStatusUpdated);
    socket.on("error", handleError);
    return () => {
      socket.off("success", handleSuccess);
      socket.off("group-channel-created", handleGroupChannelCreated);
      socket.off("message-edited", handleMessageEdited);
      socket.off("message-deleted", handleMessageDeleted);
      socket.off("channel-deleted", handleChannelDeleted);
      socket.off("channel-updated", handleChannelUpdated);
      socket.off("group-updated", handleGroupUpdated);
      socket.off("message-status-updated", handleMessageStatusUpdated);
      socket.off("error", handleError);
    };
  }, []);

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;
    socket.on("new-message", handleNewMessage);
    socket.on("user-online", handleUserOnline);
    socket.on("user-offline", handleUserOffline);
    socket.on("typing", handleTyping);
    socket.on("stop-typing", handleStopTyping);
    return () => {
      socket.off("user-online", handleUserOnline);
      socket.off("user-offline", handleUserOffline);
      socket.off("typing", handleTyping);
      socket.off("new-message", handleNewMessage);
      socket.off("stop-typing", handleStopTyping);
    };
  }, [users]);

  useEffect(() => {
    if (!showScrollToBottomRef.current) {
      scrollToBottom();
    }
  }, [chatMessages]);
  useEffect(() => {
    allContactsRef.current = allContacts;
  }, [allContacts]);

  const sameId = (a: any, b: any) => String(a) === String(b);

  // ── Socket handlers ─────────────────────────────────────────────────────────
  const handleUserOnline = ({ userId }: { userId: string }) => {
    setAllContacts((prev) =>
      prev.map((c) => (sameId(c.id, userId) ? { ...c, isOnline: true } : c)),
    );
    setUsers((prev) =>
      prev.map((u) => {
        const contains = u.members?.some((m) => sameId(m.id, userId));
        if (!contains) return u;
        if (!u.isGroup) return { ...u, isOnline: true };
        const anyOnline = u.members?.some(
          (m) =>
            !!allContactsRef.current.find((c) => sameId(c.id, m.id))?.isOnline,
        );
        return { ...u, isOnline: !!anyOnline };
      }),
    );
  };

  const handleUserOffline = ({ userId }: { userId: string }) => {
    setAllContacts((prev) =>
      prev.map((c) => (sameId(c.id, userId) ? { ...c, isOnline: false } : c)),
    );
    setUsers((prev) =>
      prev.map((u) => {
        const contains = u.members?.some((m) => sameId(m.id, userId));
        if (!contains) return u;
        if (!u.isGroup) return { ...u, isOnline: false };
        const anyOnline = u.members?.some(
          (m) =>
            !!allContactsRef.current.find((c) => sameId(c.id, m.id))?.isOnline,
        );
        return { ...u, isOnline: !!anyOnline };
      }),
    );
  };

  const handleTyping = ({
    userId,
    channelId,
    userName,
  }: {
    userId: string;
    channelId: string;
    userName: string;
  }) => {
    setUsers((prev) =>
      prev.map((u) => (u.id === channelId ? { ...u, isTyping: true } : u)),
    );
    if (channelId === activeUser?.id && userId !== currentUserId) {
      setChatMessages((prev) =>
        prev.filter((msg) => !msg.id.startsWith("typing-")),
      );
      setChatMessages((prev) => [
        ...prev,
        {
          id: `typing-${userId}-${Date.now()}`,
          senderId: userId,
          text: `${userName} is typing...`,
          time: new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
          status: "sent",
          type: "text",
        } as any,
      ]);
    }
  };

  const handleStopTyping = ({
    userId,
    channelId,
  }: {
    userId: string;
    channelId: string;
  }) => {
    setUsers((prev) =>
      prev.map((u) => (u.id === channelId ? { ...u, isTyping: false } : u)),
    );
    if (channelId === activeUser?.id) {
      setChatMessages((prev) =>
        prev.filter((msg) => !msg.id.startsWith(`typing-${userId}`)),
      );
    }
  };

  type MessageStatus = "sent" | "delivered" | "read";
  const handleMessageStatusUpdated = ({
    messageId,
    status,
    channelId,
  }: {
    messageId: string;
    status: MessageStatus;
    channelId: string;
  }) => {
    if (activeUserRef.current && String(activeUserRef.current.id) === String(channelId)) {
      setChatMessages((prev) =>
        prev.map((msg) => {
          if (
            (messageId && String(msg.id) === String(messageId)) ||
            (status === "read" && msg.senderId === currentUserId)
          ) {
            return { ...msg, status };
          }
          return msg;
        })
      );
    }
    setUsers((prev) =>
      prev.map((u) =>
        u.id === channelId
          ? {
            ...u,
            lastMessageStatus: status,
            unreadCount: status === "read" ? 0 : u.unreadCount,
          }
          : u,
      ),
    );
  };

  const handleNewMessage = async (message: any) => {
    const isSender = message.senderId.toString() === currentUserId;
    const channelId = message.channelId.toString();

    setUsers((prev) => {
      const exists = prev.some((u) => u.id === channelId);
      if (!exists) {
        const socket = getSocket();
        socket.emit("get-channel", { channelId }, (response: any) => {
          if (response.success) {
            const newChat = mapChatToChatUser(response.data, allContacts);
            setUsers((cur) =>
              cur.some((u) => u.id === newChat.id) ? cur : [newChat, ...cur],
            );
          }
        });
      }
      const mapped = prev.map((u) =>
        u.id === channelId
          ? {
            ...u,
            senderId: String(message.senderId),
            lastMessage: getCallText(message),
            time: new Date(message.createdAt).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
            lastMessageType: message.type || "text",
            unreadCount:
              isSender || u.id === activeUserRef.current?.id
                ? 0
                : (u.unreadCount || 0) + 1,
            lastMessageStatus: message.status || "sent",
          }
          : u,
      );
      const targetIdx = mapped.findIndex((u) => u.id === channelId);
      if (targetIdx > 0) {
        const item = mapped.splice(targetIdx, 1)[0];
        mapped.unshift(item);
      }
      return mapped;
    });

    const formattedMessage: ChatMessage = {
      id: message.id.toString(),
      senderId: message.senderId.toString(),
      text: message.content,
      time: new Date(message.createdAt).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
      status: message.status || "sent",
      type: message.type || "text",
      attachments: message.attachments || [],
      // Extract callDetails from metadata for call-type messages
      ...(message.type === "call" && message.metadata?.callDetails
        ? { callDetails: message.metadata.callDetails }
        : {}),
    };

    if (channelId === activeUserRef.current?.id && !isSender) {
      if (showScrollToBottomRef.current) {
        setUnreadBottomCount((prev) => prev + 1);
      }
    }

    if (channelId === activeUserRef.current?.id) {
      setChatMessages((prev) =>
        prev.some((m) => m.id === formattedMessage.id)
          ? prev
          : [...prev, formattedMessage],
      );

      if (!isSender) {
        const socket = getSocket();
        socket.emit(
          "mark-messages-read",
          { channelId: activeUserRef.current?.id },
          (response: any) => {
            if (response.success) {
              setUsers((prev) =>
                prev.map((u) =>
                  u.id === channelId
                    ? { ...u, unreadCount: 0, lastMessageStatus: "read" }
                    : u,
                ),
              );
            }
          },
        );
      }
    }

    if (
      !isSender &&
      channelId !== activeUserRef.current?.id &&
      notificationSoundRef.current
    ) {
      notificationSoundRef.current.play().catch(() => { });
    }
  };

  const handleGroupChannelCreated = (data: any) => {
    const newChat = mapChatToChatUser(data, allContacts);
    setUsers((prev) =>
      prev.some((u) => u.id === newChat.id) ? prev : [newChat, ...prev],
    );
    if (
      data.creatorId !== currentUserId &&
      newChat.id !== activeUser?.id &&
      notificationSoundRef.current
    ) {
      notificationSoundRef.current.play().catch(() => { });
    }
    if (!activeUser) {
      setActiveUser(newChat);
      setChatMessages([]);
      setHasMoreMessages(true);
      const socket = getSocket();
      socket.emit(
        "get-channel-messages",
        { channelId: newChat.id, limit: 50, offset: 0 },
        (response: any) => {
          if (response.success) {
            setChatMessages(
              response.data.messages.map((msg: any) => ({
                id: msg.id.toString(),
                senderId: msg.senderId.toString(),
                text: msg.content,
                time: new Date(msg.createdAt).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                }),
                status: msg.status || "sent",
                type: msg.type || "text",
                attachments: msg.attachments || [],
              })),
            );
            if (response.data.messages.length < 50) {
              setHasMoreMessages(false);
            }
          }
        },
      );
    }
  };

  const handleMessageEdited = (message: any) => {
    setChatMessages((prev) =>
      prev.map((msg) =>
        msg.id === message.id.toString()
          ? { ...msg, text: message.content }
          : msg,
      ),
    );
  };

  const handleMessageDeleted = ({ messageId }: { messageId: string }) => {
    setChatMessages((prev) => prev.filter((msg) => msg.id !== messageId));
  };

  const handleChannelDeleted = ({ channelId }: { channelId: string }) => {
    setUsers((prev) => prev.filter((u) => u.id !== channelId));
    if (activeUser?.id === channelId) {
      setActiveUser(null);
      setChatMessages([]);
    }
  };

  const handleChannelUpdated = (data: any) => {
    setUsers((prev) =>
      prev.map((u) =>
        u.id === data.id.toString()
          ? {
            ...u,
            members: data.memberIds.map((id: number) => {
              const c = allContacts.find((c) => c.id === id.toString());
              return {
                id: id.toString(),
                name: c ? c.name : `User ${id}`,
                avatar: c?.avatar || "",
                isOnline: c?.isOnline || false,
              };
            }),
          }
          : u,
      ),
    );
  };

  const handleGroupUpdated = (data: any) => {
    const newMembers = data.memberIds.map((id: number) => {
      const c = allContacts.find((c) => c.id === id.toString());
      return {
        id: id.toString(),
        name: c ? c.name : `User ${id}`,
        avatar: c?.avatar || "",
        isOnline: c?.isOnline || false,
      };
    });
    setUsers((prev) =>
      prev.map((u) =>
        u.id === data.id.toString()
          ? { ...u, name: data.name, avatar: data.avatar, members: newMembers }
          : u,
      ),
    );
    if (activeUser?.id === data.id.toString()) {
      setActiveUser((prev) =>
        prev
          ? {
            ...prev,
            name: data.name,
            avatar: data.avatar || prev.avatar,
            members: newMembers,
          }
          : null,
      );
    }
  };

  // ── Initial data fetch ──────────────────────────────────────────────────────
  const fetchInitialData = async () => {
    try {
      setError(null);
      setUsers([]);

      const [chatsResponse, contactsResponse] = await Promise.all([
        getUserChannels(),
        getAllUsers(),
      ]);

      const currentUserCompanyId = _user?.userDetails?.company_id;
      const currentUserCompanyType = _user?.userDetails?.account_type;


      const mappedContacts = contactsResponse.data.data.data.allUsers
        .filter((user: any) => {
          if (user.key == currentUserId) return false;

          if (user.Status !== "Active") return false;

          if (user.CompanyId !== currentUserCompanyId) return false;

          if (user.CompanyType !== currentUserCompanyType) return false;

          return true;
        })
        .map(mapUserToChatUser);

      setAllContacts(mappedContacts);

      const mappedChats = chatsResponse.data.data
        .sort((a: any, b: any) => {
          const timeA = a.lastMessage?.createdAt ? new Date(a.lastMessage.createdAt).getTime() : 0;
          const timeB = b.lastMessage?.createdAt ? new Date(b.lastMessage.createdAt).getTime() : 0;
          return timeB - timeA;
        })
        .map((chat: any) => mapChatToChatUser(chat, mappedContacts))
        .filter((chat: ChatUser) => {
          if (!chat.isGroup) {
            const otherParticipant = chat.members?.find(
              (m) => String(m.id) !== String(currentUserId)
            );
            return otherParticipant && mappedContacts.some((c: any) => c.id === otherParticipant?.id);
          }
          return true;
        });

      setUsers(mappedChats);

      if (mappedChats.length === 0) {
        setIsFirstTimeChat(true);
      } else {
        const firstChat = mappedChats[0];
        setActiveUser(firstChat);
        setChatMessages([]);
        setHasMoreMessages(true);
        const socket = getSocket();
        socket.emit(
          "get-channel-messages",
          { channelId: firstChat.id, limit: 50, offset: 0 },
          (response: any) => {
            if (response.success === true) {
              setChatMessages(
                response.data.messages.map(mapBackendMessage),
              );
              if (response.data.messages.length < 50) {
                setHasMoreMessages(false);
              }
            } else {
              setError(response.message);
            }
          },
        );
      }
    } catch (error: any) {
      console.error("Error fetching initial data:", error);
      setError("Failed to load chats. Please try again.");
    }
  };

  // ── Scroll ──────────────────────────────────────────────────────────────────
  const scrollToBottom = () =>
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });

  // ── Emoji ───────────────────────────────────────────────────────────────────
  const handleEmojiClick = (emojiData: EmojiClickData) => {
    setMessageInput((prev) => prev + emojiData.emoji);
    setShowEmojiPicker(false);
  };

  // ── Edit / Delete messages ──────────────────────────────────────────────────
  const handleEditMessage = (message: ChatMessage) => {
    setEditingMessage(message);
    setMessageInput(message.text);
  };

  const handleUpdateMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingMessage || !activeUser) return;
    const socket = getSocket();
    socket.emit(
      "edit-message",
      {
        messageId: editingMessage.id,
        channelId: activeUser.id,
        newContent: messageInput,
      },
      (response: any) => {
        if (response.success) {
          setChatMessages((prev) =>
            prev.map((msg) =>
              msg.id === editingMessage.id
                ? { ...msg, text: messageInput }
                : msg,
            ),
          );
          setEditingMessage(null);
          setMessageInput("");
        } else {
          setError(response.message);
        }
      },
    );
  };

  const handleDeleteMessage = (message: ChatMessage) => {
    setMessageToDelete(message);
    setShowDeleteModal(true);
  };

  const confirmDeleteMessage = async () => {
    if (!messageToDelete || !activeUser) return;
    const socket = getSocket();
    socket.emit(
      "delete-message",
      { messageId: messageToDelete.id, channelId: activeUser.id },
      (response: any) => {
        if (response.success) {
          setChatMessages((prev) =>
            prev.filter((msg) => msg.id !== messageToDelete.id),
          );
          setShowDeleteModal(false);
          setMessageToDelete(null);
        } else {
          setError(response.message);
        }
      },
    );
  };

  const handleDeleteChat = () => setShowDeleteChatModal(true);

  const confirmDeleteChat = async () => {
    if (!activeUser) return;
    const socket = getSocket();
    socket.emit(
      "delete-channel",
      { channelId: activeUser.id },
      (response: any) => {
        if (response.success) {
          setUsers((prev) => prev.filter((u) => u.id !== activeUser.id));
          setActiveUser(null);
          setChatMessages([]);
          setShowDeleteChatModal(false);
        } else {
          setError(response.message);
        }
      },
    );
  };

  // ── Group ───────────────────────────────────────────────────────────────────
  const handleEditGroup = () => {
    if (activeUser?.isGroup) {
      setGroupForm({
        name: activeUser.name,
        members: activeUser.members?.map((m) => m.id) || [],
        avatar: activeUser.avatar || undefined,
      });
      setGroupAvatarPreview(activeUser.avatar || null);
      setShowGroupEditModal(true);
    }
  };

  const handleUpdateGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeUser) return;
    const socket = getSocket();
    const avatarPath = await uploadGroupAvatar(groupForm.avatar);
    socket.emit(
      "update-group",
      {
        channelId: activeUser.id,
        name: groupForm.name,
        memberIds: groupForm.members,
        avatar: avatarPath || undefined,
      },
      (response: any) => {
        if (response.success) {
          setShowGroupEditModal(false);
          setGroupForm({ name: "", members: [], avatar: undefined });
          setGroupAvatarPreview((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return null;
          });
        } else {
          setError(response.message);
        }
      },
    );
  };

  const handleGroupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const socket = getSocket();
    const avatarPath = await uploadGroupAvatar(groupForm.avatar);
    socket.emit(
      "create-group-channel",
      {
        name: groupForm.name,
        memberIds: groupForm.members,
        isPrivate: true,
        avatar: avatarPath || undefined,
      },
      (response: any) => {
        if (response.success === true) {
          const newGroup = mapChatToChatUser(response.data, allContacts);
          setUsers((prev) => [newGroup, ...prev]);
          setShowGroupModal(false);
          setGroupForm({ name: "", members: [], avatar: undefined });
          setGroupAvatarPreview((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return null;
          });
          setActiveUser(newGroup);
          setChatMessages([]);
        } else {
          setError(response.error?.message || "Failed to create group");
        }
      },
    );
  };

  const toggleGroupMember = (userId: string) => {
    setGroupForm((prev) => ({
      ...prev,
      members: prev.members.includes(userId)
        ? prev.members.filter((id) => id !== userId)
        : [...prev.members, userId],
    }));
  };

  // ── Send message ────────────────────────────────────────────────────────────
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    const message = messageInput.trim();
    const files = fileInputRef.current?.files;
    if ((!message && !files) || !activeUser) return;

    let attachments: ChatAttachment[] = [];
    if (files && files.length > 0) attachments = await uploadFiles(files);

    const socket = getSocket();
    const newMessage: ChatMessage = {
      id: Date.now().toString(),
      senderId: currentUserId!,
      text: message,
      time: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
      status: "sent",
      type:
        attachments.length > 0
          ? attachments[0].type.startsWith("image/")
            ? "image"
            : attachments[0].type.startsWith("video/")
              ? "video"
              : attachments[0].type.startsWith("audio/")
                ? "audio"
                : "document"
          : "text",
      attachments,
    };

    setChatMessages((prev) => [...prev, newMessage]);
    setMessageInput("");
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    socket.emit("stop-typing", { channelId: activeUser.id });

    setUsers((prev: any) => {
      const mapped = prev.map((u: any) =>
        u.id === activeUser.id
          ? {
            ...u,
            senderId: currentUserId,
            lastMessage:
              message ||
              (attachments.length > 0 ? `📎 ${attachments[0].name}` : ""),
            time: newMessage.time,
            lastMessageType: newMessage.type,
            unreadCount: 0,
            lastMessageStatus: "sent",
          }
          : u,
      );
      const targetIdx = mapped.findIndex((u: any) => u.id === activeUser.id);
      if (targetIdx > 0) {
        const item = mapped.splice(targetIdx, 1)[0];
        mapped.unshift(item);
      }
      return mapped;
    });

    socket.emit(
      "send-message",
      { channelId: activeUser.id, content: message, attachments },
      (response: any) => {
        if (response.success) {
          setChatMessages((prev) =>
            prev.map((msg) =>
              msg.id === newMessage.id
                ? {
                  ...msg,
                  id: response.data.id.toString(),
                  status: response.data.status || "sent",
                }
                : msg,
            ),
          );
        } else {
          setChatMessages((prev) =>
            prev.filter((msg) => msg.id !== newMessage.id),
          );
          setError(response.message);
        }
      },
    );
  };

  const handleTypingInput = () => {
    if (!activeUser) return;
    const socket = getSocket();
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    socket.emit("typing", { channelId: activeUser.id });
    typingTimeoutRef.current = setTimeout(() => {
      socket.emit("stop-typing", { channelId: activeUser.id });
      typingTimeoutRef.current = null;
    }, 3000);
  };

  // ── New chat ────────────────────────────────────────────────────────────────
  const startNewChat = (contact: ChatUser) => {
    const socket = getSocket();
    socket.emit(
      "create-or-get-direct-channel",
      { targetUserId: contact.id },
      (response: any) => {
        if (response.success === true) {
          const newChat = mapChatToChatUser(response.data, allContacts);
          setUsers((prev) =>
            prev.some((u) => u.id === newChat.id) ? prev : [newChat, ...prev],
          );
          setActiveUser(newChat);
          setChatMessages([]);
          setHasMoreMessages(true);
          setShowNewChatModal(false);
          socket.emit(
            "get-channel-messages",
            { channelId: newChat.id, limit: 50, offset: 0 },
            (msgResponse: any) => {
              if (msgResponse.success === true) {
                setChatMessages(
                  msgResponse.data.messages.map(mapBackendMessage),
                );
                if (msgResponse.data.messages.length < 50) {
                  setHasMoreMessages(false);
                }
              }
            },
          );
        } else {
          setError(response.message);
        }
      },
    );
  };

  // ── Render helpers ──────────────────────────────────────────────────────────
  const renderFileAttachment = (attachment: ChatAttachment) => {
    const isImage = attachment.type.startsWith("image/");
    const isVideo = attachment.type.startsWith("video/");
    const isAudio = attachment.type.startsWith("audio/");
    return (
      <div className="message-attachment" key={attachment.path}>
        {isImage ? (
          <a
            href={`${baseUrl}chatService/${attachment.path}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src={`${baseUrl}chatService/${attachment.path}`}
              alt={attachment.name}
              className="img-thumbnail"
              style={{ maxWidth: "300px", maxHeight: "300px" }}
            />
          </a>
        ) : isVideo ? (
          <video controls style={{ maxWidth: "300px" }}>
            <source
              src={`${baseUrl}chatService/${attachment.path}`}
              type={attachment.type}
            />
          </video>
        ) : isAudio ? (
          <audio controls>
            <source
              src={`${baseUrl}chatService/${attachment.path}`}
              type={attachment.type}
            />
          </audio>
        ) : (
          <div className="document-attachment">
            <i className={getFileIcon(attachment.type)} />
            <a
              href={`${baseUrl}chatService/${attachment.path}`}
              target="_blank"
              rel="noopener noreferrer"
              download
            >
              {attachment.name}
            </a>
            <span className="file-size">
              ({formatFileSize(attachment.size)})
            </span>
          </div>
        )}
      </div>
    );
  };

  const renderFilePreview = (
    file: { name: string; size: number; type: string; preview?: string },
    index: number,
  ) => (
    <div className="file-preview-item" key={index}>
      {file.preview ? (
        <img
          src={file.preview}
          alt={file.name}
          className="file-preview-image"
        />
      ) : (
        <i className={`${getFileIcon(file.type)} file-preview-icon`} />
      )}
      <div className="file-preview-info">
        <span className="file-preview-name">{file.name}</span>
        <span className="file-preview-size">{formatFileSize(file.size)}</span>
      </div>
      <button
        className="file-preview-remove"
        type="button"
        onClick={() => removeFilePreview(index)}
      >
        <i className="ti ti-x" />
      </button>
    </div>
  );

  const renderUserItem = (user: ChatUser) => {
    const isActive = activeUser?.id === user.id;
    return (
      <motion.div
        layout
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.2 }}
        className={`chat-list ${isActive ? "active" : ""} ${user.unreadCount ? "unread" : ""} gap-3`}
        key={user.id}
        style={{ height: "100%" }}
      >
        <Link
          to="#"
          className="chat-user-list"
          onClick={() => {
            setActiveUser(user);
            const socket = getSocket();
            socket.emit(
              "mark-messages-read",
              { channelId: user.id },
              (response: any) => {
                if (response.success)
                  setUsers((prev) =>
                    prev.map((u) =>
                      u.id === user.id
                        ? { ...u, unreadCount: 0, lastMessageStatus: "read" }
                        : u,
                    ),
                  );
              },
            );
            setActiveUser(user);
            setChatMessages([]);
            setHasMoreMessages(true);
            socket.emit(
              "get-channel-messages",
              { channelId: user.id, limit: 50, offset: 0 },
              (response: any) => {
                if (response.success === true) {
                  setChatMessages(
                    response.data.messages.map(mapBackendMessage)
                  );
                  if (response.data.messages.length < 50) {
                    setHasMoreMessages(false);
                  }
                } else {
                  setError(response.message);
                }
              },
            );
          }}
        >
          <div
            className={`avatar avatar-lg ${user.isOnline ? "online" : "offline"} me-2 gap-1`}
          >
            {renderAvatar({
              src: user.avatar,
              name: user.name,
              isGroup: user.isGroup,
              size: 48,
              baseUrl,
            })}
          </div>
          <div className="chat-user-info">
            <div className="chat-user-msg">
              <h6>{user.name}</h6>
              <p>
                {user.isTyping ? (
                  <span className="animate-typing">
                    ✍️ {user?.name} is typing...
                  </span>
                ) : (
                  <>
                    {user.lastMessageType === "document" && (
                      <i className="ti ti-file me-1" />
                    )}
                    {user.lastMessageType === "call" && !user.lastMessage?.includes("📵") && !user.lastMessage?.includes("declined") && (
                      <i className="ti ti-phone-call me-1" />
                    )}
                    {user.lastMessageType === "call" && user.lastMessage?.includes("📵") && (
                      <i className="ti ti-phone-missed me-1 text-danger" />
                    )}
                    {user.lastMessageType === "call" && user.lastMessage?.includes("declined") && (
                      <i className="ti ti-phone-off me-1" />
                    )}
                    {user.lastMessageType === "video-call" && "📹 Video call"}
                    {user.lastMessageType === "image" && "🖼️ Image"}
                    {user.lastMessageType === "audio" && "🔊 Audio"}
                    <span className={user.lastMessage?.includes("📵") ? "text-danger fw-medium" : ""}>
                      {user.lastMessage ||
                        (user.isGroup ? "👋 Group created" : "👋 Say hello!")}
                    </span>
                  </>
                )}
              </p>
            </div>
            <div className="chat-user-time mb-3">
              <span className="time">{user.time || "🆕 New"}</span>
              <div className="chat-pin">
                {user.isPinned && <i className="ti ti-pin me-2" />}
                {user.isFavorite && <span className="text-warning">❤️</span>}
                {user.senderId === currentUserId && user.lastMessageStatus && ['sent', 'delivered', 'read'].includes(user.lastMessageStatus) && (
                  <i
                    className={`ti ${user.lastMessageStatus === 'sent' ? 'ti-check' : 'ti-checks'} fs-16 me-1 ${user.lastMessageStatus === 'read' ? 'text-primary' : 'text-muted'}`}
                  />
                )}
                {user.unreadCount ? (
                  <span className="count-message fs-12 fw-semibold">
                    {user.unreadCount}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        </Link>
        {!user.isGroup && (
          <div className="chat-dropdown">
            <Link className="#" to="#" data-bs-toggle="dropdown">
              <i className="ti ti-dots-vertical" />
            </Link>
            <ul className="dropdown-menu dropdown-menu-end p-3">

              <>
                <li>
                  <Link
                    className={`dropdown-item ${isCallActive ? "text-muted" : ""}`}
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (isCallActive) return;
                      handleStartCall(user);
                    }}
                    style={isCallActive ? { cursor: "not-allowed", opacity: 0.5, pointerEvents: "none" } : {}}
                    title={isCallActive ? "Another call in progress" : "Audio Call"}
                  >
                    <i className="ti ti-phone me-2" />
                    Audio Call
                  </Link>
                </li>
                <li>
                  <Link
                    className="dropdown-item text-danger"
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveUser(user);
                      handleDeleteChat();
                    }}
                  >
                    <i className="ti ti-trash me-2" />
                    Delete Chat
                  </Link>
                </li>
              </>
            </ul>
          </div>
        )}
      </motion.div>
    );
  };

  const renderMessage = (message: ChatMessage) => {
    const isCurrentUser = message.senderId == currentUserId;
    const user = allContacts.find((c) => c.id == message.senderId);
    const messageTime = new Date();
    const [time, period] = message.time.split(" ");
    const [hours, minutes] = time.split(":").map(Number);
    messageTime.setHours(
      period === "PM" && hours !== 12 ? hours + 12 : hours,
      minutes,
    );
    const isEditable =
      (new Date().getTime() - messageTime.getTime()) / (1000 * 60) <= 30;

    if (message.id.startsWith("typing-")) {
      return (
        <div className="chats" key={message.id}>
          <div className="chat-avatar">
            {renderAvatar({ src: user?.avatar, name: user?.name, size: 40 })}
          </div>
          <div className="chat-content">
            <div className="chat-profile-name">
              <h6>
                {user?.name}
                <i className="ti ti-circle-filled fs-7 mx-2" />
                <span className="chat-time">{message.time}</span>
              </h6>
            </div>
            <div className="message-content">
              <span className="animate-typing">✍️ typing...</span>
            </div>
          </div>
        </div>
      );
    }

    if (message.type === "call") {
      const callStatus = message.callDetails?.callStatus;
      const callerId = message.callDetails?.callerId;
      const iAmTheCaller = String(callerId) === String(currentUserId);

      // Determine the "other" person shown in the bubble
      const otherPersonInCall = iAmTheCaller
        ? activeUser
        : allContacts.find((c) => c.id === callerId);

      // Missed call perspective:
      let callIcon = "ti ti-phone-call";
      let callBg = "#e3f2fd";
      let callTextColor = "#1565c0";
      let callLabel = message.text;

      if (callStatus === "missed") {
        callIcon = "ti ti-phone-missed";
        callBg = "#ffebee";
        callTextColor = "#c62828";
        callLabel = iAmTheCaller ? "no answer" : "Miss call";
      } else if (callStatus === "answered") {
        callIcon = "ti ti-phone-check";
        callBg = "#e8f5e9";
        callTextColor = "#2e7d32";
        callLabel = message.text; // e.g. "Audio call · 01:23"
      } else if (callStatus === "rejected") {
        callIcon = "ti ti-phone-off";
        callBg = "#fafafa";
        callTextColor = "#616161";
        callLabel = iAmTheCaller ? "Call Rejected" : "You declined the call";
      }

      return (
        <div
          className={`chats ${isCurrentUser ? "chats-right" : ""}`}
          key={message.id}
          style={{ marginBottom: 4 }}
        >
          <div>
            {/* Show caller name above bubble for group context clarity - only for non-current users and only in groups */}
            {activeUser?.isGroup && !isCurrentUser && (
              <div className="chat-profile-name" style={{ paddingBottom: 4 }}>
                <h6 style={{ margin: 0, fontSize: 12, color: getSenderColor(user?.name) }}>
                  {user?.name || "Unknown Caller"}
                </h6>
              </div>
            )}

            <div
              className="message-content call-message"
              style={{
                backgroundColor: callBg,
                color: callTextColor,
                padding: "9px 14px",
                borderRadius: "12px",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                minWidth: 160,
              }}
            >
              <i className={callIcon} style={{ fontSize: 16 }} />
              <span style={{ fontWeight: 500, fontSize: 13 }}>{callLabel}</span>

              {/* Show time in bubble for all messages */}
              <span
                className="chat-time"
                style={{
                  fontSize: 10,
                  marginLeft: 'auto',
                  opacity: 0.7,
                  whiteSpace: 'nowrap'
                }}
              >
                {message.time}
              </span>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        className={`chats ${isCurrentUser ? "chats-right" : ""}`}
        key={message.id}
        style={{
          textAlign: isCurrentUser ? "right" : "left",
          background: isCurrentUser ? "" : "#F8F9FA",
          padding: isCurrentUser ? "" : "12px",
          borderRadius: isCurrentUser ? "" : "10px",
        }}
      >
        <div>
          {activeUser?.isGroup && !isCurrentUser && (
            <div className="chat-profile-name" style={{ paddingBottom: 4 }}>
              <h6 style={{ margin: 0, fontSize: 12, color: getSenderColor(user?.name) }}>
                {user?.name || "Unknown User"}
              </h6>
            </div>
          )}
          <div>
            {message.attachments?.map((att) => renderFileAttachment(att))}
            {message.text && (
              <div className="message-content" style={{ width: "100%" }}>
                {message.text}
                <div>
                  <span className="chat-time" style={{ fontSize: "10px" }}>
                    {message.time}
                    {isCurrentUser && (
                      <span
                        className="message-status"
                        style={{ marginLeft: "1px" }}
                      >
                        {message.status === "read" ? (
                          <i className="ti ti-checks text-success" style={{ fontSize: "16px" }} />
                        ) : message.status === "delivered" ? (
                          <i className="ti ti-checks text-white-50" style={{ fontSize: "16px" }} />
                        ) : (
                          <i className="ti ti-check text-white-50" style={{ fontSize: "16px" }} />
                        )}
                      </span>
                    )}
                  </span>
                </div>
              </div>
            )}
            {isCurrentUser && isEditable && (
              <div className="chat-action-btns ms-3">
                <div className="chat-action-col">
                  {message.text && (
                    <Link
                      className="#"
                      to="#"
                      onClick={() => handleEditMessage(message)}
                    >
                      <i className="ti ti-edit" />
                    </Link>
                  )}
                  <Link
                    className="#"
                    to="#"
                    onClick={() => handleDeleteMessage(message)}
                  >
                    <i className="ti ti-trash" />
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  // ✅ NOTE: NO call modals here — the GlobalCallWidget in index.tsx handles ALL call UI
  return (
    <>
      {/* ── Main Chat UI ──────────────────────────────────────────────────── */}
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">💬 Chat</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item" />
                  <li className="breadcrumb-item">Application</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Chat
                  </li>
                </ol>
              </nav>
            </div>
            <div className="head-icons">
              <CollapseHeader />
            </div>
          </div>

          {error && <div className="alert alert-danger">⚠️ {error}</div>}

          <div
            className="chat-wrapper"
            style={{ height: "calc(100vh - 130px)" }}
          >
            {/* ── Sidebar ──────────────────────────────────────────────────── */}
            <div className="sidebar-group">
              <div id="chats" className="sidebar-content active slimscroll">
                <Scrollbars>
                  <div className="chat-search-header">
                    <div className="header-title d-flex align-items-center justify-content-between mb-3">
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => setShowNewChatModal(true)}
                        >
                          <i className="ti ti-message-plus me-1" /> New Chat
                        </button>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => setShowGroupModal(true)}
                        >
                          <i className="ti ti-users me-1" /> New Group
                        </button>
                      </div>
                    </div>
                    <div>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="🔍 Search For Chats"
                          value={recentChatsSearchTerm}
                          onChange={(e) =>
                            setRecentChatsSearchTerm(e.target.value)
                          }
                        />
                      </div>
                    </div>
                  </div>
                  <div className="sidebar-body chat-body" id="chatsidebar">
                    <div className="chat-users-wrap">
                      {isFirstTimeChat ? (
                        <div className="first-time-chat">
                          <h5>👋 Start a new conversation</h5>
                          <p>Tap new chat to start a new conversation</p>
                        </div>
                      ) : (
                        <AnimatePresence>
                          {memoizedUsers.map((user) => renderUserItem(user))}
                        </AnimatePresence>
                      )}
                    </div>
                  </div>
                </Scrollbars>
              </div>
            </div>

            {/* ── Chat window ───────────────────────────────────────────────── */}
            <div className="chat chat-messages show" id="middle">
              <div>
                {activeUser ? (
                  <>
                    <div
                      className="chat-header"
                    >
                      <div className="user-details">
                        <div className="d-xl-none">
                          <Link className="text-muted chat-close me-2" to="#">
                            <i className="fas fa-arrow-left" />
                          </Link>
                        </div>
                        <div
                          className={`avatar avatar-lg ${activeUser.isOnline ? "online" : "offline"} flex-shrink-0`}
                        >
                          {renderAvatar({
                            src: activeUser.avatar,
                            name: activeUser.name,
                            isGroup: !!activeUser.isGroup,
                            size: 48,
                            baseUrl,
                          })}
                        </div>
                        <div className="ms-2 overflow-hidden">
                          <h6>{activeUser.name}</h6>
                          <span
                            className="last-seen"
                            style={{ cursor: activeUser.isGroup ? "pointer" : "default" }}
                            onClick={() => {
                              if (activeUser.isGroup) {
                                setShowGroupMembersModal(true);
                              }
                            }}
                          >
                            {activeUser.isGroup
                              ? `${activeUser.members?.length || 0} Members`
                              : ""}
                          </span>
                        </div>
                      </div>
                      <div className="chat-options">
                        <ul>
                          {!activeUser.isGroup && (
                            <li>
                              <button
                                className="btn no-bg"
                                onClick={() => handleStartCall(activeUser)}
                                disabled={isCallActive}
                                style={{
                                  background: "transparent",
                                  border: "none",
                                  color: isCallActive ? "rgba(255,255,255,0.3)" : "white",
                                  padding: "8px",
                                  cursor: isCallActive ? "not-allowed" : "pointer",
                                }}
                                title={isCallActive ? "Another call in progress" : "Audio Call"}
                              >
                                <i className="ti ti-phone" />
                              </button>
                            </li>
                          )}
                          <li>
                            <Link
                              className="btn no-bg"
                              to="#"
                              data-bs-toggle="dropdown"
                            >
                              <i className="ti ti-dots-vertical" />
                            </Link>
                            <ul className="dropdown-menu dropdown-menu-end p-3"  >
                              {activeUser?.isGroup &&
                                activeUser.createdBy == currentUserId && (
                                  <>
                                    <li>
                                      <Link
                                        to="#"
                                        className="dropdown-item"
                                        onClick={handleEditGroup}
                                      >
                                        <i className="ti ti-edit me-2" />
                                        Edit Group
                                      </Link>
                                    </li>
                                    <li>
                                      <Link
                                        to="#"
                                        className="dropdown-item"
                                        onClick={handleEditGroup}
                                      >
                                        <i className="ti ti-users me-2" />
                                        Manage Members
                                      </Link>
                                    </li>
                                  </>
                                )}
                              <li >
                                <Link
                                  to="#"
                                  className="dropdown-item text-danger"
                                  onClick={handleDeleteChat}
                                >
                                  <i className="ti ti-trash me-2" />
                                  Delete Chat
                                </Link>
                              </li>
                            </ul>
                          </li>
                        </ul>
                      </div>
                    </div>

                    <div className="chat-body chat-page-group slimscroll">
                      <Scrollbars
                        ref={scrollRef}
                        onScrollFrame={(values) => {
                          const isScrolledUp = values.top < 0.95;
                          setShowScrollToBottom(isScrolledUp);
                          if (!isScrolledUp) {
                            setUnreadBottomCount(0);
                          }

                          if (values.top <= 0.05 && hasMoreMessages && !isLoadingMore && activeUser) {
                            setIsLoadingMore(true);
                            // Capture the current scroll height before adding new elements
                            const previousScrollHeight = scrollRef.current ? scrollRef.current.getScrollHeight() : 0;
                            const nextOffset = chatMessages.length;
                            const socket = getSocket();
                            socket.emit(
                              "get-channel-messages",
                              { channelId: activeUser.id, limit: 3, offset: nextOffset },
                              (response: any) => {
                                if (response.success) {
                                  const olderMessages = response.data.messages.map((msg: any) => {
                                    // Same reconstruction logic as before:
                                    let parsedType = msg.type || "text";
                                    let callDetailsMerged = null;

                                    if (msg.metadata) {
                                      try {
                                        const meta = typeof msg.metadata === "string"
                                          ? JSON.parse(msg.metadata)
                                          : msg.metadata;
                                        if (meta.callDetails) {
                                          callDetailsMerged = meta.callDetails;
                                          parsedType = "call";
                                        }
                                      } catch (e) {
                                        console.error("Failed to parse message metadata", e);
                                      }
                                    } else if (msg.attachments && msg.attachments.length > 0) {
                                      const firstAtt = msg.attachments[0];
                                      if (firstAtt.type.startsWith("image/")) parsedType = "image";
                                      else if (firstAtt.type.startsWith("video/")) parsedType = "video";
                                      else if (firstAtt.type.startsWith("audio/")) parsedType = "audio";
                                      else parsedType = "document";
                                    }

                                    return {
                                      id: msg.id.toString(),
                                      senderId: msg.senderId.toString(),
                                      text: msg.content,
                                      time: new Date(msg.createdAt).toLocaleTimeString([], {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      }),
                                      status: msg.status || "sent",
                                      type: parsedType,
                                      attachments: msg.attachments || [],
                                      callDetails: callDetailsMerged,
                                    };
                                  });

                                  setChatMessages((prev) => [...olderMessages, ...prev]);
                                  if (olderMessages.length < 3) {
                                    setHasMoreMessages(false);
                                  }

                                  // Restore scroll position after React renders the new messages
                                  setTimeout(() => {
                                    if (scrollRef.current) {
                                      const newScrollHeight = scrollRef.current.getScrollHeight();
                                      scrollRef.current.scrollTop(newScrollHeight - previousScrollHeight);
                                    }
                                  }, 50);
                                }
                                setIsLoadingMore(false);
                              }
                            );
                          }
                        }}
                        style={{ height: "100%", overflowX: "hidden" }}
                        renderTrackVertical={({ style, ...props }) => (
                          <div
                            {...props}
                            style={{ ...style, display: "block", right: "2px", bottom: "2px", top: "2px", borderRadius: "3px", width: "5px" }}
                          />
                        )}
                        renderThumbVertical={({ style, ...props }) => (
                          <div
                            {...props}
                            style={{ ...style, display: "block", backgroundColor: "rgba(0,0,0,0.2)", borderRadius: "3px" }}
                          />
                        )}
                        renderTrackHorizontal={({ style, ...props }) => (
                          <div
                            {...props}
                            style={{ ...style, display: "none" }}
                          />
                        )}
                        renderThumbHorizontal={({ style, ...props }) => (
                          <div
                            {...props}
                            style={{ ...style, display: "none" }}
                          />
                        )}
                      >
                        <div
                          className="messages"
                          style={{
                            wordBreak: "break-word",
                            whiteSpace: "normal",
                          }}
                        >
                          {isLoadingMore && (
                            <div className="text-center py-2">
                              <div className="spinner-border spinner-border-sm text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                              </div>
                            </div>
                          )}
                          {!isLoadingMore && hasMoreMessages && chatMessages.length > 0 && (
                            <div className="text-center py-2 mb-3">
                              <button
                                className="btn btn-sm btn-light rounded-pill border shadow-sm px-3"
                                onClick={() => {
                                  if (!activeUser || isLoadingMore || !hasMoreMessages) return;
                                  setIsLoadingMore(true);
                                  const previousScrollHeight = scrollRef.current ? scrollRef.current.getScrollHeight() : 0;
                                  const nextOffset = chatMessages.length;
                                  const socket = getSocket();
                                  socket.emit(
                                    "get-channel-messages",
                                    { channelId: activeUser.id, limit: 50, offset: nextOffset },
                                    (response: any) => {
                                      if (response.success) {
                                        const olderMessages = response.data.messages.map((msg: any) => {
                                          let parsedType = msg.type || "text";
                                          let callDetailsMerged = null;
                                          if (msg.metadata) {
                                            try {
                                              const meta = typeof msg.metadata === "string" ? JSON.parse(msg.metadata) : msg.metadata;
                                              if (meta.callDetails) {
                                                callDetailsMerged = meta.callDetails;
                                                parsedType = "call";
                                              }
                                            } catch (e) { console.error("Failed to parse message metadata", e); }
                                          } else if (msg.attachments && msg.attachments.length > 0) {
                                            const firstAtt = msg.attachments[0];
                                            if (firstAtt.type.startsWith("image/")) parsedType = "image";
                                            else if (firstAtt.type.startsWith("video/")) parsedType = "video";
                                            else if (firstAtt.type.startsWith("audio/")) parsedType = "audio";
                                            else parsedType = "document";
                                          }
                                          return {
                                            id: msg.id.toString(),
                                            senderId: msg.senderId.toString(),
                                            text: msg.content,
                                            time: new Date(msg.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
                                            status: msg.status || "sent",
                                            type: parsedType,
                                            attachments: msg.attachments || [],
                                            callDetails: callDetailsMerged,
                                          };
                                        });
                                        setChatMessages((prev) => [...olderMessages, ...prev]);
                                        if (olderMessages.length < 50) setHasMoreMessages(false);
                                        setTimeout(() => {
                                          if (scrollRef.current) {
                                            const newScrollHeight = scrollRef.current.getScrollHeight();
                                            scrollRef.current.scrollTop(newScrollHeight - previousScrollHeight);
                                          }
                                        }, 50);
                                      }
                                      setIsLoadingMore(false);
                                    }
                                  );
                                }}
                              >
                                <i className="ti ti-loader me-1"></i> Load previous messages
                              </button>
                            </div>
                          )}
                          {chatMessages.length > 0 ? (
                            <>
                              {chatMessages.map((message) =>
                                renderMessage(message),
                              )}
                              <div ref={messagesEndRef} />
                            </>
                          ) : (
                            <div
                              className="no-chat-selected text-center py-5"
                              style={{ alignSelf: "center" }}
                            >
                              <div className="empty-state-icon mb-3">
                                <i className="ti ti-message-circle fs-1 text-muted" />
                              </div>
                              <h5>No messages yet</h5>
                              <p className="text-muted">
                                👋 Say hello to start the conversation!
                              </p>
                            </div>
                          )}
                        </div>
                      </Scrollbars>

                      {showScrollToBottom && (
                        <div
                          style={{
                            position: "absolute",
                            bottom: "115px",
                            right: "20px",
                            zIndex: 1000,
                          }}
                        >
                          <button
                            className="btn btn-primary rounded-circle d-flex align-items-center justify-content-center p-0"
                            style={{
                              width: "42px",
                              height: "42px",
                              minWidth: "42px",
                              boxShadow: "0 6px 16px rgba(0,0,0,0.2)",
                              border: "none",
                              transition: "all 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                              cursor: "pointer",
                              position: "relative",
                            }}
                            onClick={() => {
                              scrollToBottom();
                              setShowScrollToBottom(false);
                              setUnreadBottomCount(0);
                            }}
                            title="Scroll to bottom"
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = "scale(1.1) translateY(-2px)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "scale(1) translateY(0)";
                            }}
                          >
                            <i className="ti ti-chevrons-down fs-3 " />
                            {unreadBottomCount > 0 && (
                              <span
                                className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                                style={{ fontSize: "10px", padding: "4px 6px" }}
                              >
                                {unreadBottomCount > 99 ? "99+" : unreadBottomCount}
                              </span>
                            )}
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="chat-footer position-sticky">
                      <form
                        className="chat-footer-form"
                        onSubmit={
                          editingMessage
                            ? handleUpdateMessage
                            : handleSendMessage
                        }
                      >
                        {filePreviews.length > 0 && (
                          <div className="file-preview-container">
                            <div className="file-preview-scroll">
                              {filePreviews.map((file, i) =>
                                renderFilePreview(file, i),
                              )}
                            </div>
                          </div>
                        )}
                        <div className="chat-footer-input-group">
                          <div className="chat-input-wrapper">
                            <input
                              type="text"
                              className="form-control chat-input"
                              placeholder="Type your message..."
                              value={messageInput}
                              onChange={(e) => {
                                setMessageInput(e.target.value);
                                handleTypingInput();
                              }}
                            />
                            {showEmojiPicker && (
                              <div
                                className="emoji-picker-container"
                                ref={emojiPickerRef}
                              >
                                <EmojiPicker
                                  onEmojiClick={handleEmojiClick}
                                  width={300}
                                  height={350}
                                />
                              </div>
                            )}
                          </div>
                          <div className="chat-footer-actions">
                            <button
                              type="button"
                              className="chat-footer-action"
                              onClick={() =>
                                setShowEmojiPicker(!showEmojiPicker)
                              }
                            >
                              <i className="ti ti-mood-smile" />
                            </button>
                            <div className="chat-footer-file">
                              <label
                                htmlFor="files"
                                className="chat-footer-action"
                              >
                                <i className="ti ti-folder" />
                              </label>
                              <input
                                type="file"
                                className="chat-file-input"
                                name="files"
                                id="files"
                                multiple
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.zip,.rar"
                              />
                            </div>
                            <div className="chat-footer-buttons">
                              {editingMessage ? (
                                <>
                                  <button
                                    className="btn btn-primary btn-sm chat-action-btn"
                                    type="submit"
                                    disabled={uploadingFiles}
                                  >
                                    <i className="ti ti-check me-1" /> Update
                                  </button>
                                  <button
                                    className="btn btn-outline-danger btn-sm chat-action-btn icon-only"
                                    type="button"
                                    onClick={() => {
                                      setEditingMessage(null);
                                      setMessageInput("");
                                      setFilePreviews([]);
                                    }}
                                  >
                                    <i className="ti ti-x" />
                                  </button>
                                </>
                              ) : (
                                <button
                                  className="btn btn-primary btn-sm chat-action-btn send-button"
                                  type="submit"
                                  disabled={
                                    uploadingFiles ||
                                    (!messageInput.trim() &&
                                      filePreviews.length === 0)
                                  }
                                >
                                  <i className="ti ti-send" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </form>
                    </div>
                  </>
                ) : (
                  <div className="no-chat-selected text-center py-5">
                    <div className="empty-state-icon mb-3">
                      <i className="ti ti-message-circle fs-1 text-muted" />
                    </div>
                    <h5>
                      {isFirstTimeChat
                        ? "Select a contact to start chatting"
                        : "Select a chat to view messages"}
                    </h5>
                    <p className="text-muted">
                      {isFirstTimeChat
                        ? "👋 Start by selecting a contact or creating a new group"
                        : "💬 Choose a conversation from the sidebar"}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Group Create Modal ────────────────────────────────────────────── */}
      <Modal show={showGroupModal} onHide={() => setShowGroupModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>👥 Create New Group</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleGroupSubmit}>
            <div className="mb-3">
              <label className="form-label">Group Name</label>
              <input
                type="text"
                className="form-control"
                value={groupForm.name}
                onChange={(e) =>
                  setGroupForm({ ...groupForm, name: e.target.value })
                }
                required
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Group Avatar</label>
              <div className="group-avatar-upload">
                {groupAvatarPreview && (
                  <div className="avatar-preview">
                    <img
                      src={groupAvatarPreview}
                      alt="Preview"
                      className="rounded-circle"
                      style={{ width: "60px", height: "60px" }}
                    />
                  </div>
                )}
                <label
                  htmlFor="groupAvatar"
                  className="btn btn-outline-primary btn-sm"
                >
                  <i className="ti ti-upload me-1" /> Upload Avatar
                </label>
                <input
                  type="file"
                  className="d-none"
                  id="groupAvatar"
                  ref={groupAvatarInputRef}
                  onChange={handleGroupAvatarChange}
                  accept="image/*"
                />
              </div>
            </div>
            <div className="mb-3">
              <input
                type="text"
                className="form-control mb-2"
                placeholder="Search members..."
                value={groupSearchTerm}
                onChange={(e) => setGroupSearchTerm(e.target.value)}
              />
              <div className="d-flex flex-wrap gap-1 mb-2">
                {alphabet.map((letter) => (
                  <button
                    type="button"
                    key={letter}
                    className={`btn btn-sm ${selectedLetter === letter ? "btn-primary" : "btn-outline-secondary"}`}
                    onClick={() => setSelectedLetter(letter)}
                  >
                    {letter}
                  </button>
                ))}
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => setSelectedLetter("")}
                >
                  Clear
                </button>
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">Select Members</label>
              <div className="group-members-list d-flex flex-wrap gap-2">
                {filteredContacts.map((contact) => (
                  <div
                    key={contact.id}
                    className={`member-item ${groupForm.members.includes(contact.id) ? "selected" : ""}`}
                    onClick={() => toggleGroupMember(contact.id)}
                    style={{
                      width: "18%",
                      minWidth: "120px",
                      cursor: "pointer",
                    }}
                  >
                    <div className="avatar avatar-sm me-2">
                      {renderAvatar({
                        src: contact.avatar,
                        name: contact.name,
                        size: 32,
                      })}
                    </div>
                    <span>{contact.name}</span>
                    {groupForm.members.includes(contact.id) && (
                      <i className="ti ti-check" />
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowGroupModal(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={
                  groupForm.members.length === 0 ||
                  !groupForm.name ||
                  uploadingGroupAvatar
                }
              >
                Create Group
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      {/* ── Group Edit Modal ──────────────────────────────────────────────── */}
      <Modal
        show={showGroupEditModal}
        onHide={() => setShowGroupEditModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>👥 Manage Group</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleUpdateGroup}>
            <div className="mb-3">
              <label className="form-label">Group Name</label>
              <input
                type="text"
                className="form-control"
                value={groupForm.name}
                onChange={(e) =>
                  setGroupForm({ ...groupForm, name: e.target.value })
                }
                required
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Group Avatar</label>
              <div className="group-avatar-upload">
                {groupAvatarPreview && (
                  <div className="avatar-preview">
                    <img
                      src={groupAvatarPreview}
                      alt="Preview"
                      className="rounded-circle"
                      style={{ width: "60px", height: "60px" }}
                    />
                  </div>
                )}
                <label
                  htmlFor="groupAvatarEdit"
                  className="btn btn-outline-primary btn-sm"
                >
                  <i className="ti ti-upload me-1" />{" "}
                  {groupAvatarPreview ? "Change Avatar" : "Upload Avatar"}
                </label>
                <input
                  type="file"
                  className="d-none"
                  id="groupAvatarEdit"
                  ref={groupAvatarInputRef}
                  onChange={handleGroupAvatarChange}
                  accept="image/*"
                />
              </div>
            </div>
            <div className="mb-3">
              <input
                type="text"
                className="form-control mb-2"
                placeholder="Search members..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <div className="d-flex flex-wrap gap-1 mb-2">
                {alphabet.map((letter) => (
                  <button
                    type="button"
                    key={letter}
                    className={`btn btn-sm ${selectedLetter === letter ? "btn-primary" : "btn-outline-secondary"}`}
                    onClick={() => setSelectedLetter(letter)}
                  >
                    {letter}
                  </button>
                ))}
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => setSelectedLetter("")}
                >
                  Clear
                </button>
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">Manage Members</label>
              <div className="group-members-list d-flex flex-wrap gap-2">
                {filteredContacts
                  .filter((c) => c.id !== currentUserId)
                  .map((contact) => (
                    <div
                      key={contact.id}
                      className={`member-item ${groupForm.members.includes(contact.id) ? "selected" : ""}`}
                      onClick={() => toggleGroupMember(contact.id)}
                      style={{
                        width: "18%",
                        minWidth: "120px",
                        cursor: "pointer",
                      }}
                    >
                      <div className="avatar avatar-sm me-2">
                        {renderAvatar({
                          src: contact.avatar,
                          name: contact.name,
                          size: 32,
                        })}
                      </div>
                      <span>{contact.name}</span>
                      {groupForm.members.includes(contact.id) && (
                        <i className="ti ti-check" />
                      )}
                    </div>
                  ))}
              </div>
            </div>
            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowGroupEditModal(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={
                  groupForm.members.length === 0 ||
                  !groupForm.name ||
                  uploadingGroupAvatar
                }
              >
                Update Group
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      {/* ── New Chat Modal ────────────────────────────────────────────────── */}
      <Modal
        show={showNewChatModal}
        onHide={() => {
          setShowNewChatModal(false);
          setContactSearchTerm("");
          setSelectedLetter("");
        }}
        dialogClassName="custom-modal-lg"
      >
        <Modal.Header closeButton className="border-b border-gray-200">
          <Modal.Title className="text-xl font-semibold text-gray-900">
            💬 Start New Chat
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-6 py-4">
          <div className="mb-2">
            <div className="relative">
              <input
                type="text"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl"
                placeholder="🔍 Search contacts..."
                value={contactSearchTerm}
                onChange={(e) => setContactSearchTerm(e.target.value)}
              />
              {contactSearchTerm && (
                <button
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  onClick={() => setContactSearchTerm("")}
                >
                  <i className="ti ti-x text-lg" />
                </button>
              )}
            </div>
          </div>
          <div className="flex flex-row gap-4">
            <div className="alphabet-container mb-2 d-flex w-16 max-h-[400px] overflow-y-auto flex flex-col items-center gap-2">
              {alphabet.map((letter) => (
                <button
                  key={letter}
                  className={`alphabet-btn border rounded-lg ${selectedLetter === letter ? "bg-orange-500 text-white border-orange-500" : "bg-white"}`}
                  onClick={() =>
                    setSelectedLetter(selectedLetter === letter ? "" : letter)
                  }
                >
                  {letter}
                </button>
              ))}
              <button
                className="alphabet-btn border rounded-lg bg-white"
                onClick={() => setSelectedLetter("")}
              >
                Clear
              </button>
            </div>
            <div className="contacts-list text-center d-flex flex-1 max-h-[400px] overflow-y-auto rounded-lg border border-gray-200 bg-white">
              {filteredContacts.length > 0 ? (
                filteredContacts.map((contact, index) => (
                  <div
                    key={contact.id}
                    className={`px-3 py-3 cursor-pointer chat-member ${index !== filteredContacts.length - 1 ? "border-b border-gray-200" : ""}`}
                    onClick={() => startNewChat(contact)}
                    role="button"
                    tabIndex={0}
                  >
                    <div
                      className={`avatar ${contact.isOnline ? "online" : "offline"} mr-4`}
                    >
                      {renderAvatar({
                        src: contact.avatar,
                        name: contact.name,
                        size: 40,
                        roundedClass: "rounded-full",
                      })}
                    </div>
                    <div className="flex-1">
                      <h6 className="font-medium text-gray-900 text-base mb-0.5">
                        {contact.name}
                      </h6>
                      <p className="text-sm text-gray-500">
                        {contact.isOnline ? "🟢 Online" : "⚪ Offline"}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-10">
                  <i className="ti ti-users text-5xl text-gray-400 mb-3" />
                  <p className="text-gray-500">
                    {contactSearchTerm || selectedLetter
                      ? "No contacts found"
                      : "Type or select a letter to search"}
                  </p>
                </div>
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-t border-gray-200">
          <button
            className="px-4 py-2 btn btn-primary rounded-lg"
            onClick={() => {
              setShowNewChatModal(false);
              setContactSearchTerm("");
              setSelectedLetter("");
            }}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>

      {/* ── Group Members Modal ───────────────────────────────────────────── */}
      <Modal
        show={showGroupMembersModal}
        onHide={() => setShowGroupMembersModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Group Members</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          <div className="list-group list-group-flush" style={{ maxHeight: "60vh", overflowY: "auto" }}>
            {activeUser?.members?.map((member) => (
              <div
                key={member.id}
                className="list-group-item d-flex align-items-center justify-content-between p-3"
              >
                <div className="d-flex align-items-center gap-3">
                  <div className="avatar avatar-md">
                    {renderAvatar({
                      src: member.avatar,
                      name: member.name,
                      isGroup: false,
                      size: 40,
                      baseUrl,
                    })}
                  </div>
                  <h6 className="mb-0 fs-15">{/^User\s*\d+$/.test(member.name) ? "Me" : member.name}</h6>
                </div>
                {member.id == activeUser.createdBy && (
                  <span className="badge bg-primary-transparent text-primary px-2 py-1 rounded">
                    Admin
                  </span>
                )}
              </div>
            ))}
          </div>
        </Modal.Body>
      </Modal>

      {/* ── Delete Message Modal ──────────────────────────────────────────── */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Message</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this message? This action cannot be
          undone.
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-end gap-2">
          <button
            className="btn btn-secondary"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </button>
          <button className="btn btn-danger" onClick={confirmDeleteMessage}>
            Delete
          </button>
        </Modal.Footer>
      </Modal>

      {/* ── Delete Chat Modal ─────────────────────────────────────────────── */}
      <Modal
        show={showDeleteChatModal}
        onHide={() => setShowDeleteChatModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete Chat</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this chat? All messages will be
          permanently removed.
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-end gap-2">
          <button
            className="btn btn-secondary"
            onClick={() => setShowDeleteChatModal(false)}
          >
            Cancel
          </button>
          <button className="btn btn-danger" onClick={confirmDeleteChat}>
            Delete Chat
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Chat;
